import { useState } from "react";
import { api } from "../api/client";
import type { FeatureRequestRow } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { EmptyView, ErrorView, LoadingView } from "../components/StateView";
import { absoluteTime, relativeTime, shortDeviceId } from "../format";
import { useApi } from "../useApi";
import styles from "./BugReportsPage.module.css";

/**
 * Feature requests share the bug-report list/detail layout — same
 * shape minus the diagnostics + screenshot sections, so we re-use
 * `BugReportsPage.module.css` instead of duplicating the styles.
 */
export function FeatureRequestsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const state = useApi(() => api.feed("feature-request", { limit: 500 }), []);

  const selected =
    state.kind === "ready"
      ? state.data.rows.find((r) => r.id === selectedId) ?? null
      : null;

  return (
    <>
      <PageHeader
        title="Feature requests"
        subtitle="What users want next, newest first."
      />

      {state.kind === "loading" ? <LoadingView /> : null}
      {state.kind === "error" ? (
        <ErrorView message={state.message} onRetry={state.reload} />
      ) : null}
      {state.kind === "ready" ? (
        state.data.rows.length === 0 ? (
          <EmptyView label="No feature requests yet." />
        ) : (
          <div className={styles.layout}>
            <ul className={styles.list}>
              {state.data.rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={
                      r.id === selectedId
                        ? `${styles.row} ${styles.rowActive}`
                        : styles.row
                    }
                  >
                    <span className={styles.rowTitle}>{r.title}</span>
                    <span className={styles.rowMeta}>
                      <span title={absoluteTime(r.created_at)}>
                        {relativeTime(r.created_at)}
                      </span>
                      {r.suite_version ? <span> · v{r.suite_version}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <section className={styles.detail}>
              {selected ? (
                <FeatureDetail row={selected} />
              ) : (
                <div className={styles.detailPlaceholder}>
                  Select a request to view details.
                </div>
              )}
            </section>
          </div>
        )
      ) : null}
    </>
  );
}

function FeatureDetail({ row }: { row: FeatureRequestRow }) {
  return (
    <article>
      <h2 className={styles.detailTitle}>{row.title}</h2>
      <dl className={styles.metaList}>
        <div className={styles.metaRow}>
          <dt className={styles.metaLabel}>Submitted</dt>
          <dd className={styles.metaValue}>{absoluteTime(row.created_at)}</dd>
        </div>
        <div className={styles.metaRow}>
          <dt className={styles.metaLabel}>Suite</dt>
          <dd className={styles.metaValue}>{row.suite_version ?? "—"}</dd>
        </div>
        <div className={styles.metaRow}>
          <dt className={styles.metaLabel}>OS</dt>
          <dd className={styles.metaValue}>
            {`${row.os ?? "—"} ${row.arch ?? ""}`.trim()}
          </dd>
        </div>
        <div className={styles.metaRow}>
          <dt className={styles.metaLabel}>Device</dt>
          <dd className={styles.metaValue}>{shortDeviceId(row.device_id)}</dd>
        </div>
      </dl>
      <h3 className={styles.sectionHeading}>Description</h3>
      <pre className={styles.body}>{row.description}</pre>
    </article>
  );
}
